import { AbsoluteFill, useCurrentFrame, useVideoConfig, spring, interpolate } from "remotion";
import React from "react";

// VFX: Network Graph
// Category: bar-charts · Tags: ui, network, graph, diagram, architecture
const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

type Node = { id: string; x: number; y: number; label: string; color?: string };
type Edge = { from: string; to: string };

const NetworkGraph: React.FC<{
  nodes?: Node[]; edges?: Edge[]; centerNodeId?: string; nodeSize?: number;
}> = ({
  nodes = [
    { id: "c", x: 50, y: 50, label: "API", color: "#a855f7" },
    { id: "a", x: 25, y: 25, label: "Auth", color: "#10b981" },
    { id: "b", x: 75, y: 25, label: "UI", color: "#ec4899" },
    { id: "d", x: 25, y: 75, label: "DB", color: "#f59e0b" },
    { id: "e", x: 75, y: 75, label: "Hooks", color: "#06b6d4" },
  ],
  edges = [{ from: "a", to: "c" }, { from: "b", to: "c" }, { from: "c", to: "d" }, { from: "c", to: "e" }],
  centerNodeId = "c", nodeSize = 32
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", background: "#0a0e1a" }}>
      <div style={{ position: "relative", width: 560, height: 680 }}>
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} viewBox="0 0 560 680">
          {edges.map(({ from, to }, i) => {
            const nf = nodeMap[from], nt = nodeMap[to];
            if (!nf || !nt) return null;
            const delay = 8 + i * 5;
            const p = spring({ frame: frame - delay, fps, config: { damping: 200, stiffness: 120 } });
            const fx = nf.x / 100 * 560, fy = nf.y / 100 * 680, tx = nt.x / 100 * 560, ty = nt.y / 100 * 680;
            return (
              <line key={i} x1={fx} y1={fy}
                x2={fx + (tx - fx) * p} y2={fy + (ty - fy) * p}
                stroke="rgba(255,255,255,0.25)" strokeWidth={2} />
            );
          })}
        </svg>
        {nodes.map((node, i) => {
          const delay = i * 6;
          const p = spring({ frame: frame - delay, fps, config: { damping: 150, stiffness: 200 } });
          const isCenter = node.id === centerNodeId;
          const size = isCenter ? nodeSize * 1.5 : nodeSize;
          return (
            <div key={node.id} style={{
              position: "absolute",
              left: `calc(${node.x}% - ${size}px)`,
              top: `calc(${node.y}% - ${size}px)`,
              opacity: p,
              transform: `scale(${interpolate(p, [0, 1], [0.3, 1], clamp)})`,
            }}>
              <div style={{
                width: size * 2, height: size * 2, borderRadius: "50%",
                background: node.color || "#a855f7",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: `0 0 ${isCenter ? 32 : 16}px ${node.color || "#a855f7"}aa`,
              }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#fff", fontFamily: "system-ui", letterSpacing: "0.5px", textTransform: "uppercase" }}>{node.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

export default function UiNetworkGraphAnimated1500Ms() {
  return <NetworkGraph />;
}

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
